import { useLocation } from "@remix-run/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { queryClient } from "~/api/queryClient";
import { trackingPublicConfigQueryOptions } from "~/lib/tracking/config";
import {
  OPEN_CONSENT_EVENT,
  effectiveConsent,
  isChoiceCurrent,
  onConsentChange,
  readStoredConsent,
  saveConsent,
} from "~/lib/tracking/consent";
import {
  applyConsent,
  hasBrowserVendors,
  identifyUser,
  setTrackingConfig,
  setTrackingPath,
  trackPageView,
} from "~/lib/tracking/tracker";
import { useCurrentUserStore } from "~/modules/common/store/useCurrentUserStore";

import { ConsentBanner } from "./ConsentBanner";

import type { ConsentVendorNames } from "./ConsentBanner";
import type { ConsentCategories } from "~/lib/tracking/consent";
import type { TrackingPublicConfig } from "~/lib/tracking/tracker";

function vendorNames(config: TrackingPublicConfig | null): ConsentVendorNames {
  if (!config) return { analytics: [], ads: [] };
  const analytics = [
    config.ga4MeasurementId && "Google Analytics",
    (config.posthogKey || import.meta.env.VITE_POSTHOG_KEY) && "PostHog",
    config.clarityProjectId && "Microsoft Clarity",
  ].filter(Boolean) as string[];
  const ads = [
    config.metaPixelId && "Meta (Facebook e Instagram)",
    config.tiktokPixelId && "TikTok",
    config.googleAdsTagId && "Google Ads",
    config.xPixelId && "X",
    config.linkedinPartnerId && "LinkedIn",
  ].filter(Boolean) as string[];
  return { analytics, ads };
}

/**
 * Orquesta la medición del navegador: trae la config pública, aplica el consentimiento, carga
 * las plataformas permitidas, cuenta las páginas vistas y muestra el aviso de cookies cuando
 * hace falta. Sin ids configurados no carga nada ni muestra nada.
 *
 * Con el aviso de cookies apagado (`consentBanner: false`, el default) se mide desde el primer
 * render: no hay aviso, el panel de preferencias no se abre y sólo cuenta una elección explícita
 * de la versión vigente (el «No medir en este navegador» de /privacy).
 */
export function TrackingManager() {
  const location = useLocation();
  const userId = useCurrentUserStore((state) => state.currentUser?.id ?? null);
  const [config, setConfig] = useState<TrackingPublicConfig | null | undefined>(undefined);
  const [banner, setBanner] = useState<{
    mode: "summary" | "preferences";
    dismissible: boolean;
  } | null>(null);
  const [current, setCurrent] = useState<ConsentCategories>({ analytics: false, ads: false });
  const configRef = useRef<TrackingPublicConfig | null | undefined>(undefined);
  configRef.current = config;

  // Sin esperar a que el hilo quede libre: con el aviso apagado, la primera página vista cuenta.
  // Los scripts de las plataformas cargan `async`, así que no traban el render.
  useEffect(() => {
    let cancelled = false;
    void queryClient
      .fetchQuery(trackingPublicConfigQueryOptions)
      .then((value) => {
        if (!cancelled) setConfig(value ?? null);
      })
      .catch(() => {
        if (!cancelled) setConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (config === undefined) return;
    setTrackingConfig(config);
    setTrackingPath(window.location.pathname);
    if (!config) return;

    const bannerOn = config.consentBanner === true;
    const stored = readStoredConsent();
    // Aviso apagado = ya está decidido (se mide salvo un "no" vigente de /privacy).
    const decided = isChoiceCurrent(stored, config.consentVersion) || !bannerOn;
    const consent = effectiveConsent(
      stored,
      config.consentVersion,
      config.consentDefault,
      bannerOn,
    );
    setCurrent(consent);
    applyConsent(consent, decided);

    const needsConsent = hasBrowserVendors(config) || config.serverDestinations.length > 0;
    if (bannerOn && !decided && needsConsent) setBanner({ mode: "summary", dismissible: false });
  }, [config]);

  useEffect(() => {
    if (config) trackPageView(location.pathname);
    else setTrackingPath(location.pathname);
  }, [config, location.pathname, location.search]);

  useEffect(() => {
    void identifyUser(userId);
  }, [userId]);

  useEffect(() => {
    // Con el aviso apagado no hay panel que abrir (el botón del footer ni se muestra).
    const open = () => {
      if (configRef.current?.consentBanner !== true) return;
      setBanner({ mode: "preferences", dismissible: true });
    };
    window.addEventListener(OPEN_CONSENT_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
  }, []);

  // Una elección hecha fuera del aviso (el «No medir en este navegador» de /privacy) se aplica en
  // el acto, sin recargar.
  useEffect(
    () =>
      onConsentChange((choice) => {
        setCurrent(choice);
        applyConsent(choice, true);
      }),
    [],
  );

  const onSave = useCallback(
    (choice: ConsentCategories) => {
      setBanner(null);
      setCurrent(choice);
      applyConsent(choice, true);
      void saveConsent(choice, config?.consentVersion ?? "", "banner");
    },
    [config],
  );

  const onDismiss = useCallback(() => setBanner(null), []);
  const onModeChange = useCallback(
    (mode: "summary" | "preferences") => setBanner((prev) => (prev ? { ...prev, mode } : prev)),
    [],
  );

  if (!banner) return null;

  return (
    <ConsentBanner
      mode={banner.mode}
      dismissible={banner.dismissible}
      initial={current}
      vendors={vendorNames(config ?? null)}
      onSave={onSave}
      onModeChange={onModeChange}
      onDismiss={onDismiss}
    />
  );
}
